import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import {
  MatchListItemFragment,
  RefereeDetailItemFragment,
  SeasonFragment,
} from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import PersonHeader from '@amf/shared/components/person/PersonHeader';
import TeamMatches from '@amf/shared/components/team/TeamMatches';
import { getMatches, LIMIT } from '@amf/shared/data/Match';
import Container from '@amf/shared/components/layout/Container';
import RefereeStats from '@amf/shared/components/referee/RefereeStats';
import { format } from 'date-fns';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';
import { AppLinks } from 'utils/links';

interface RefereeDetailPageProps extends NavigationProps {
  referee: RefereeDetailItemFragment;
  seasons: SeasonFragment[];
  matches: MatchListItemFragment[];
}

function RefereeDetailPage({
  referee,
  seasons,
  matches,
  navigation,
  partners,
}: RefereeDetailPageProps) {
  const { t } = useTranslate();
  return (
    <div className='RefereeDetailPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={referee.fullName} image={referee.photoUrl} />
        <PersonHeader
          person={referee}
          title={t('referee.detail.page')}
          prefixes={{ list: AppLinks.referee }}
        >
          <ul>
            <li>
              <label>Licence</label>
              <strong>{t(`referee.license.${referee.license}`)}</strong>
            </li>
            <li>
              <label>Pohlaví</label>
              <strong>{t(`player.info.gender.${referee.gender}`)}</strong>
            </li>
            <li>
              <label>Věk</label>
              <strong>{t('player.info.age', { age: referee.age })}</strong>
            </li>
          </ul>
        </PersonHeader>
        <Container>
          <TeamMatches
            title={t('referee.matches.future.title')}
            seasons={seasons}
            referee={referee}
            matches={matches}
            matchPrefix={AppLinks.match}
            dateFrom={format(new Date(), 'yyyy-MM-dd')}
          />
          <TeamMatches
            title={t('referee.matches.title')}
            seasons={seasons}
            referee={referee}
            matches={matches}
            matchPrefix={AppLinks.match}
            dateTo={format(new Date(), 'yyyy-MM-dd')}
            orderDesc
          />
          <RefereeStats title={t('referee.stats.title')} referee={referee} />
        </Container>
      </Layout>
    </div>
  );
}

export default RefereeDetailPage;

export const getServerSideProps: GetServerSideProps<RefereeDetailPageProps, { id: string }> =
  async ({ locale, params, res }) => {
    const { id } = params;

    const { referee } = await sdk.RefereeDetail({ union: process.env.NEXT_PUBLIC_UNION, id });
    if (!referee) return { notFound: true };

    res.setHeader('Cache-Control', 'max-age=0, s-maxage=3600');

    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });

    const filteredSeasons = seasons.filter(s => s.leagues.length > 0);
    let matches: MatchListItemFragment[] = [];
    if (filteredSeasons.length > 0) {
      matches = await getMatches(
        filteredSeasons[0].leagues[0].id,
        0,
        LIMIT,
        null,
        null,
        null,
        null,
        null,
        null,
        referee.id,
      );
    }

    return {
      props: {
        seasons,
        matches,
        referee,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
